import { HttpClient } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import highContrastColors from '@assets/themes/high-contrast.theme.json';
import { getFontCssUrl } from '@core/utils/font.util';
import { TinyColor } from '@ctrl/tinycolor';
import { AnswerModuleResponse } from '@shared/models/answer-module-response';
import {
  ThemeConfig,
  ThemeParameters,
} from '@shared/models/themes/theme-config';
import {
  blendColorsWithAlpha,
  calculateWCAGContrast,
  colorsAreSimilar,
  detectRedColor,
  getBestContrastColor,
  meetsWCAGRequirements,
} from '@shared/utils/color.util';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private API_URL: string;
  private MEDIA_URL: string;
  private MEDIA_SERVER: string;
  private _highContrast = false;
  private renderer: Renderer2;
  private styleElement: HTMLStyleElement | null = null;
  private themeSubject = new BehaviorSubject<ThemeConfig | null>(null);
  private highContrastSubject = new BehaviorSubject<boolean>(false);
  private styleMap = new Map<string, string>();
  private fontStylesheetLinks = new Map<string, HTMLLinkElement>();
  private highContrastTheme: ThemeConfig | null = null;
  private isLoadingHighContrastTheme = false;
  private themeCache = new Map<string, ThemeConfig>();

  highContrastChange = this.highContrastSubject.asObservable();

  hasTheme = new BehaviorSubject<boolean>(false);

  get highContrast(): boolean {
    return this._highContrast;
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private rendererFactory: RendererFactory2,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.API_URL = `${this.configService.get('ANSWER_SERVICE_API_URL')}/api/theme`;
    this.MEDIA_URL = this.configService.get('MEDIA_SERVICE_URL') || '';
    this.MEDIA_SERVER = this.MEDIA_URL.split('/media')[0] || this.MEDIA_URL;
  }

  getTheme(): Observable<ThemeConfig | null> {
    return this.themeSubject.asObservable();
  }

  private toRgbaString(color: string): string {
    if (!color) return 'rgba(0, 0, 0, 1)';

    const colorObj = new TinyColor(color);

    if (!colorObj.isValid) {
      console.warn('Invalid color format:', color);
      return 'rgba(0, 0, 0, 1)';
    }

    return colorObj.toRgbString();
  }

  private removeOpacity(color: string): string {
    if (!color) return 'rgb(0, 0, 0)';

    const colorObj = new TinyColor(color);

    if (!colorObj.isValid) {
      console.warn('Invalid color format:', color);
      return 'rgb(0, 0, 0)';
    }

    const rgb = colorObj.toRgb();
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  }

  darkenRGBA(color: string, amount: number): string {
    const colorObj = new TinyColor(color);

    if (!colorObj.isValid) {
      console.warn('Invalid color format:', color);
      return color;
    }

    // Darken by percentage (amount/255 * 100 to convert from 0-255 to percentage)
    const percentage = (amount / 255) * 100;
    return colorObj.darken(percentage).toRgbString();
  }

  private getContrastColor(
    backgroundColor: string,
    darkColor: string = '#000000',
    lightColor: string = '#ffffff',
  ): string {
    return getBestContrastColor(backgroundColor, darkColor, lightColor);
  }

  private colorsAreSimilar(
    color1: string,
    color2: string,
    threshold: number = 50,
  ): boolean {
    return colorsAreSimilar(color1, color2, threshold);
  }

  private detectRedColor(color: string): {
    isRed: boolean;
    intensity: number;
    redComponent: number;
  } {
    return detectRedColor(color);
  }

  private calculateWCAGContrast(color1: string, color2: string): number {
    return calculateWCAGContrast(color1, color2);
  }

  private meetsWCAGRequirements(
    backgroundColor: string,
    textColor: string,
  ): boolean {
    return meetsWCAGRequirements(backgroundColor, textColor, 'AA');
  }

  private ensureColorContrast(
    backgroundColor: string,
    textColor: string,
    threshold: number = 50,
  ): string {
    if (this.colorsAreSimilar(backgroundColor, textColor, threshold)) {
      const contrastColor = this.getContrastColor(backgroundColor);
      return contrastColor;
    }
    return textColor;
  }

  loadTheme(themeId: string): void {
    // If high contrast mode is active, don't load the regular theme
    if (this._highContrast) {
      if (this.highContrastTheme) {
        this.applyHighContrastTheme(this.highContrastTheme);
      }
      return;
    }

    if (themeId && themeId !== '00000000-0000-0000-0000-000000000000') {
      this.getThemeConfig(themeId)
        .pipe(
          tap((theme) => {
            if (!theme) {
              this.hasTheme.next(false);
              this.setHighContrast();
              return;
            }
            this.hasTheme.next(true);
            this.applyTheme(theme);
            this.themeSubject.next(theme);
          }),
        )
        .subscribe();
    } else {
      this.hasTheme.next(false);
      this.setHighContrast();
    }
  }

  loadThemeParameters(
    themeParameters: ThemeParameters,
    customerId?: string,
  ): void {
    if (this._highContrast) {
      if (this.highContrastTheme) {
        this.applyHighContrastTheme(this.highContrastTheme);
      }
      return;
    }

    if (themeParameters) {
      const themeConfig: ThemeConfig = {
        id: undefined,
        parameters: themeParameters,
        customerId: customerId,
      };
      this.hasTheme.next(true);
      this.applyTheme(themeConfig);
      this.themeSubject.next(themeConfig);
    } else {
      this.hasTheme.next(false);
      this.setHighContrast();
    }
  }

  syncQuestWidthToHighContrastTheme(themeParameters: ThemeParameters): void {
    if (!this.highContrastTheme) {
      return;
    }

    this.highContrastTheme.parameters = {
      ...this.highContrastTheme.parameters,
      questWidth: themeParameters.questWidth
        ? { ...themeParameters.questWidth }
        : undefined,
    };
  }

  getThemeSurvey(): Observable<AnswerModuleResponse> {
    return this.http.get<AnswerModuleResponse>(
      `${this.configService.get('ANSWER_SERVICE_API_URL')}/api/theme_survey`,
    );
  }

  transformThemeResponse(response: any): ThemeConfig {
    const transformedParams = response?.parameters?.reduce(
      (
        acc: { [x: string]: any },
        param: { name: string | number; items: any[] },
      ) => {
        if (
          !param.items ||
          !Array.isArray(param.items) ||
          param.items.length === 0
        ) {
          acc[param.name] = {};
          return acc;
        }
        acc[param.name] = param.items.reduce(
          (itemAcc, item) => {
            itemAcc[item.key] = item.value;
            return itemAcc;
          },
          {} as Record<string, string>,
        );
        return acc;
      },
      {} as Record<string, Record<string, string>>,
    );

    return {
      id: response?.id,
      customerId: response?.customerId,
      parameters: transformedParams,
    };
  }

  toggleHighContrast(): void {
    this.removeAllDynamicStyles();
    document.body.removeAttribute('style');
    this._highContrast = !this._highContrast;

    if (this._highContrast) {
      this.loadAndApplyHighContrastTheme(this.themeSubject.value?.parameters);
    } else {
      this.applyTheme(this.themeSubject.value!);
    }

    this.highContrastSubject.next(this._highContrast);
  }

  private loadAndApplyHighContrastTheme(
    parameters: ThemeParameters | undefined,
  ): void {
    if (this.highContrastTheme) {
      this.applyHighContrastTheme(this.highContrastTheme);
      return;
    }

    if (this.isLoadingHighContrastTheme) {
      return;
    }

    // Get the high contrast theme ID from config
    const themeId = this.configService.get('HIGH_CONTRAST_THEME_ID');

    if (!themeId || themeId === '00000000-0000-0000-0000-000000000000') {
      this.setHighContrast();
      return;
    }

    this.isLoadingHighContrastTheme = true;
    this.getThemeConfig(themeId)
      .pipe(
        tap((theme) => {
          if (!theme) {
            this.isLoadingHighContrastTheme = false;
            this.setHighContrast();
            return;
          }
          this.highContrastTheme = theme;
          theme.parameters.questWidth = parameters?.questWidth; // Preserve quest width from the original theme
          theme.parameters.customCss = parameters?.customCss; // Preserve custom CSS from the original theme
          this.applyHighContrastTheme(theme);
          this.isLoadingHighContrastTheme = false;
        }),
        catchError((err) => {
          console.warn(
            'Failed to load high contrast theme from API, using fallback',
            err,
          );
          this.isLoadingHighContrastTheme = false;
          this.setHighContrast();
          return of(null);
        }),
      )
      .subscribe();
  }

  private getThemeConfig(themeId: string): Observable<ThemeConfig | null> {
    const cachedTheme = this.themeCache.get(themeId);
    if (cachedTheme) {
      return of(cachedTheme);
    }

    return this.http.get<ThemeConfig>(`${this.API_URL}/${themeId}`).pipe(
      map((theme) => {
        const transformedTheme = this.transformThemeResponse(theme);
        this.themeCache.set(themeId, transformedTheme);
        return transformedTheme;
      }),
      catchError(() => of(null)),
    );
  }

  private applyHighContrastTheme(theme: ThemeConfig): void {
    this._highContrast = false;
    this.applyTheme(theme);
    this._highContrast = true;

    const { parameters } = theme;
    if (parameters) {
      const highlightColor = this.toRgbaString(
        parameters.highlight?.color || '#000000',
      );
      const bgColor = this.toRgbaString(
        parameters.background?.color || '#FFFFFF',
      );

      const activeTextColor = this.getContrastColor(highlightColor);

      this.addDynamicClass('button', {
        'background-color': highlightColor,
      });

      this.addDynamicClass('outline-button', {
        'background-color': bgColor,
        border: `1px solid ${highlightColor}`,
        color: highlightColor,
      });

      const darkenedItemBg = `${this.darkenRGBA(highlightColor, 30)} !important`;
      this.addDynamicClass(
        'ng-option-marked',
        {
          'background-color': darkenedItemBg,
        },
        false,
      );

      document.documentElement.style.setProperty(
        '--highlight-color',
        highlightColor,
      );
      document.documentElement.style.setProperty(
        '--active-text-color',
        activeTextColor,
      );
      document.documentElement.style.setProperty(
        '--highlight-border-color',
        highlightColor,
      );
      document.documentElement.style.setProperty(
        '--outline-color',
        highlightColor,
      );

      const primaryButtonTextColor = this.getContrastColor(highlightColor);
      document.documentElement.style.setProperty(
        '--primary-button-text-color',
        primaryButtonTextColor,
      );

      if (parameters.customCss?.globalCss) {
        this.applyCustomCss(parameters);
      }
    }
  }

  private setHighContrast(): void {
    this._highContrast = true;
    const { primary, secondary, background, progressBar } = highContrastColors;

    // Apply body styles
    Object.assign(document.body.style, {
      color: primary,
      borderColor: primary,
      backgroundColor: background,
      accentColor: primary,
    });

    // Set CSS custom properties
    const bgColor = this.toRgbaString(background);
    const cssVars = {
      '--body-text-color': primary,
      '--focus-color': primary,
      '--validation-color': primary,
      '--bg-color': bgColor,
      '--bg-no-opacity': bgColor,
      '--item-bg-color': bgColor,
      '--highlight-color': bgColor,
      '--highlight-border-color': secondary,
      '--outline-color': secondary,
      '--active-text-color': primary,
      '--primary-button-text-color': primary,
      '--title-color': primary,
      '--disabled-text-color': primary,
    };

    Object.entries(cssVars).forEach(([prop, value]) => {
      document.documentElement.style.setProperty(prop, value);
    });

    // Add dynamic classes
    this.addDynamicClass('bg-primary', { 'background-color': secondary });
    this.addDynamicClass('button', { 'background-color': secondary });
    this.addDynamicClass('outline-button', {
      'background-color': background,
      border: `1px solid ${secondary}`,
      color: secondary,
    });
    this.addDynamicClass('gap', { gap: '4.5rem' });
    this.addDynamicClass('hr', { 'border-color': 'black', opacity: '0.65' });

    // ng-select options with darkened backgrounds
    const bgColorWithImportant = `${bgColor} !important`;
    const darkenedBg20 = `${this.darkenRGBA(bgColor, 20)} !important`;
    const darkenedBg30 = `${this.darkenRGBA(bgColor, 30)} !important`;

    this.addDynamicClass(
      'ng-option',
      { 'background-color': bgColorWithImportant },
      false,
    );
    this.addDynamicClass(
      'ng-option:hover',
      { 'background-color': darkenedBg20 },
      false,
    );
    this.addDynamicClass(
      'ng-option-marked',
      { 'background-color': darkenedBg30 },
      false,
    );

    // Progress bar styles
    const progressStyles = {
      color: progressBar.foregroundColor,
      'background-color': progressBar.backgroundColor,
    };
    this.addDynamicClass('progress', progressStyles, false, false);
    this.addDynamicClass(
      'progress::-webkit-progress-bar',
      { 'background-color': progressBar.backgroundColor },
      false,
      false,
    );
    this.addDynamicClass(
      'progress::-webkit-progress-value',
      { 'background-color': progressBar.foregroundColor },
      false,
      false,
    );
    this.addDynamicClass(
      'progress::-moz-progress-bar',
      { 'background-color': progressBar.foregroundColor },
      false,
      false,
    );

    // K-upload button
    this.addDynamicClass(
      'k-upload-button',
      {
        'background-color': `${background} !important`,
        color: `${primary} !important`,
        border: `2px solid ${primary} !important`,
      },
      false,
    );
  }

  private applyTheme(theme: ThemeConfig): void {
    const { parameters } = theme;
    this.removeAllDynamicStyles();
    this.applyBackground(parameters);
    this.applyBackgroundImage(parameters);
    this.applySystemStyles(parameters);
    this.applyTitleFont(parameters, theme?.customerId);
    this.applyBodyFont(parameters, theme?.customerId);
    this.applyValidation(parameters);
    this.applyLayout(parameters);
    this.applyProgressbar(parameters);
    this.applyQuestWidth(parameters);
    this.applyFontSize(parameters);
    this.applyCustomCss(parameters);
  }

  private readonly questWidthVars: Record<
    string,
    { lgStart: string; lgSpan: string; xxlStart: string; xxlSpan: string }
  > = {
    slim: { lgStart: '2', lgSpan: '4', xxlStart: '4', xxlSpan: '6' },
    medium: { lgStart: '1', lgSpan: '6', xxlStart: '3', xxlSpan: '8' },
    wide: { lgStart: '1', lgSpan: '6', xxlStart: '2', xxlSpan: '10' },
  };

  private applyQuestWidth(p: ThemeConfig['parameters']): void {
    const value = p.questWidth?.size ?? 'slim';
    const { lgStart, lgSpan, xxlStart, xxlSpan } =
      this.questWidthVars[value] ?? this.questWidthVars['slim'];

    const vars: Record<string, string> = {
      '--survey-form-col-start-lg': lgStart,
      '--survey-form-col-span-lg': lgSpan,
      '--survey-form-col-start-2xl': xxlStart,
      '--survey-form-col-span-2xl': xxlSpan,
    };

    Object.entries(vars).forEach(([prop, val]) =>
      document.documentElement.style.setProperty(prop, val),
    );
  }

  private readonly fontSizeMap: Record<string, string> = {
    small: '12px',
    medium: '14px',
    large: '16px',
  };

  private applyFontSize(p: ThemeConfig['parameters']): void {
    const size = p.fontSize?.size ?? 'large';
    const px = this.fontSizeMap[size] ?? this.fontSizeMap['large'];
    document.documentElement.style.setProperty('--base-font-size', px);
  }

  private applyBackground(p: ThemeConfig['parameters']): void {
    if (p.background) {
      const bgColor = this.toRgbaString(p.background.color);
      const itemBgColor = this.removeOpacity(p.background.color);

      let dropdownBgColor = itemBgColor;
      if (p.system?.itemColor) {
        const itemColorObj = new TinyColor(p.system.itemColor);
        const bgColorObj = new TinyColor(p.background.color);

        if (itemColorObj.isValid && bgColorObj.isValid) {
          try {
            dropdownBgColor = blendColorsWithAlpha(
              bgColorObj.toRgbString(),
              itemColorObj.toRgbString(),
            );
          } catch (error) {
            console.error('Error blending colors:', error);
            dropdownBgColor = itemBgColor;
          }
        }
      }

      this.addDynamicClass('bg', {
        'background-color': bgColor,
      });
      const noOpacityBgColor = this.removeOpacity(p.background.color);
      document.documentElement.style.setProperty(
        '--bg-no-opacity',
        noOpacityBgColor,
      );

      document.documentElement.style.setProperty('--bg-color', bgColor);
      document.documentElement.style.setProperty(
        '--dropdown-bg-color',
        dropdownBgColor,
      );

      this.addDynamicClass(
        'k-input-inner',
        {
          'background-color': `${bgColor} !important`,
        },
        false,
      );

      this.addDynamicClass(
        'k-icon-button',
        {
          'background-color': `${bgColor} !important`,
        },
        false,
      );

      this.addDynamicClass(
        'ng-option',
        {
          'background-color': dropdownBgColor + ' !important',
        },
        false,
      );
      this.addDynamicClass(
        'ng-option:hover',
        {
          'background-color': this.darkenRGBA(bgColor, 20) + ' !important',
        },
        false,
      );
      this.addDynamicClass(
        'ng-option-marked',
        {
          'background-color': this.darkenRGBA(bgColor, 30) + ' !important',
        },
        false,
      );

      document.body.style.backgroundColor = bgColor;
    }
  }

  private applyBackgroundImage(p: ThemeConfig['parameters']): void {
    if (p.background?.mediaUrl) {
      let bgImageUrl = `url(${this.MEDIA_SERVER}${p.background?.mediaUrl})`;
      if (p.background?.baseUrl?.length) {
        bgImageUrl = `url(${p.background?.baseUrl}${p.background?.mediaUrl})`;
      }
      const bgColor = p.background.color;

      this.addDynamicClass(
        'background-image',
        {
          'background-image': bgImageUrl,
          'background-size': 'cover',
          'background-position': 'center',
          'background-repeat': 'no-repeat',
        },
        true,
        true,
      );
      const noOpacityBgColor = this.removeOpacity(bgColor);
      document.body.style.backgroundColor = noOpacityBgColor;
    }
  }

  private applyTitleFont(
    p: ThemeConfig['parameters'],
    customerId?: string,
  ): void {
    if (p.titleFont) {
      const textColor = this.toRgbaString(p.text.color);
      this.ensureFontStylesheet(
        p.titleFont.fontFamily,
        p.titleFont.customFont,
        customerId,
      );

      document.documentElement.style.setProperty(
        '--title-font-family',
        p.titleFont.fontFamily,
      );
      document.documentElement.style.setProperty('--title-color', textColor);

      this.addDynamicClass('title', {
        color: textColor,
        'font-family': p.titleFont.fontFamily,
      });
      this.addDynamicClass('bg-primary', { 'background-color': textColor });
    }
  }

  private applyBodyFont(
    p: ThemeConfig['parameters'],
    customerId?: string,
  ): void {
    if (p.bodyFont) {
      const bodyTextColor = this.toRgbaString(p.text.color);
      const highlightColor = this.toRgbaString(p.highlight.color);
      this.ensureFontStylesheet(
        p.bodyFont.fontFamily,
        p.bodyFont.customFont,
        customerId,
      );

      Object.assign(document.body.style, {
        fontFamily: p.bodyFont.fontFamily,
        color: bodyTextColor,
        accentColor: bodyTextColor,
      });

      const cssVars = {
        '--body-text-color': bodyTextColor,
        '--focus-color': highlightColor,
        '--outline-color': highlightColor,
      };

      Object.entries(cssVars).forEach(([prop, value]) => {
        document.documentElement.style.setProperty(prop, value);
      });

      this.addDynamicClass('hr', {
        'border-color': bodyTextColor,
        opacity: '0.4',
      });
      this.addDynamicClass('outline-button', {
        color: bodyTextColor,
        'border-color': bodyTextColor,
        'background-color': highlightColor,
      });
    }
  }

  private applyLayout(p: ThemeConfig['parameters']): void {
    if (p.layout) {
      this.addDynamicClass('gap', {
        gap: p.layout.contentSpacing === 'airy' ? '4.5rem' : '2.5rem',
      });
    }
  }

  private applyValidation(p: ThemeConfig['parameters']): void {
    const RED_COLOR = '#D11717';
    const WHITE_COLOR = '#FFFFFF';
    const BLACK_COLOR = '#000000';

    const backgroundRgba = this.toRgbaString(p.background.color);
    const redRgba = this.toRgbaString(RED_COLOR);
    const wcagCompliant = meetsWCAGRequirements(backgroundRgba, redRgba);

    let validationColor: string;
    if (!wcagCompliant) {
      const whiteColor = this.toRgbaString(WHITE_COLOR);
      const blackColor = this.toRgbaString(BLACK_COLOR);

      const whiteContrastRatio = this.calculateWCAGContrast(
        backgroundRgba,
        whiteColor,
      );
      const blackContrastRatio = this.calculateWCAGContrast(
        backgroundRgba,
        blackColor,
      );

      validationColor =
        whiteContrastRatio > blackContrastRatio ? WHITE_COLOR : BLACK_COLOR;
    } else {
      validationColor = RED_COLOR;
    }

    this.addDynamicClass('validation', { color: validationColor });
    document.documentElement.style.setProperty(
      '--validation-color',
      validationColor,
    );
  }

  private applyProgressbar(p: ThemeConfig['parameters']): void {
    const bgColor = this.toRgbaString(p.background.color);
    const highlightColor = this.toRgbaString(p.highlight.color);

    const progressStyles = {
      color: highlightColor,
      'background-color': bgColor,
    };

    this.addDynamicClass('progress', progressStyles, false, false);
    this.addDynamicClass(
      'progress::-webkit-progress-bar',
      progressStyles,
      false,
      false,
    );
    this.addDynamicClass(
      'progress::-webkit-progress-value',
      {
        color: highlightColor,
        'background-color': highlightColor,
      },
      false,
      false,
    );
    this.addDynamicClass(
      'progress::-moz-progress-bar',
      {
        color: highlightColor,
        'background-color': highlightColor,
      },
      false,
      false,
    );
  }

  private applySystemStyles(p: ThemeConfig['parameters']): void {
    if (this._highContrast) {
      return;
    }

    if (p.system?.itemColor) {
      const itemBgColor = this.toRgbaString(p.system.itemColor);
      const darkenedItemBg = `${this.darkenRGBA(itemBgColor, 30)} !important`;

      document.documentElement.style.setProperty(
        '--item-bg-color',
        itemBgColor,
      );

      this.addDynamicClass('button', { 'background-color': itemBgColor });
      this.addDynamicClass('outline-button', {
        'background-color': itemBgColor,
      });
      this.addDynamicClass(
        'ng-select-container',
        {
          'background-color': `${itemBgColor} !important`,
        },
        false,
      );
      this.addDynamicClass(
        'ng-option-marked',
        {
          'background-color': darkenedItemBg,
        },
        false,
      );
    }

    if (p.system?.disabledTextColor) {
      document.documentElement.style.setProperty(
        '--disabled-text-color',
        this.toRgbaString(p.system.disabledTextColor),
      );
    }

    const highlightColor = this.toRgbaString(p.highlight.color);
    const textColor = this.toRgbaString(p.text.color);

    document.documentElement.style.setProperty(
      '--highlight-color',
      highlightColor,
    );
    document.documentElement.style.setProperty('--title-color', textColor);

    const activeTextColor = this.ensureColorContrast(
      highlightColor,
      textColor,
      300,
    );
    document.documentElement.style.setProperty(
      '--active-text-color',
      activeTextColor,
    );

    this.addDynamicClass(
      'k-upload-button',
      {
        'background-color': `${highlightColor} !important`,
        color: `${activeTextColor} !important`,
      },
      false,
    );

    const primaryButtonTextColor = this.meetsWCAGRequirements(
      highlightColor,
      textColor,
    )
      ? textColor
      : getBestContrastColor(highlightColor);
    document.documentElement.style.setProperty(
      '--primary-button-text-color',
      primaryButtonTextColor,
    );
  }

  private applyCustomCss(p: ThemeConfig['parameters']): void {
    if (!p.customCss?.globalCss) {
      return;
    }

    const css = p.customCss?.globalCss;

    if (!this.styleElement) {
      this.styleElement = this.renderer.createElement('style');
      this.renderer.appendChild(document.head, this.styleElement);
    }

    if (this.styleElement) {
      this.styleElement.innerHTML += '\n' + css;
    }
  }

  private removeAllDynamicStyles(): void {
    this.styleMap.clear();
    if (this.styleElement) {
      this.styleElement.innerHTML = '';
    }

    // Clear all CSS custom properties to prevent old theme colors from persisting
    const cssVarsToRemove = [
      '--body-text-color',
      '--focus-color',
      '--validation-color',
      '--bg-color',
      '--bg-no-opacity',
      '--item-bg-color',
      '--highlight-color',
      '--highlight-border-color',
      '--outline-color',
      '--active-text-color',
      '--primary-button-text-color',
      '--title-color',
      '--disabled-text-color',
      '--title-font-family',
      '--dropdown-bg-color',
    ];

    cssVarsToRemove.forEach((varName) => {
      document.documentElement.style.removeProperty(varName);
    });
  }

  private addDynamicClass(
    className: string,
    styles: Record<string, string>,
    dynamic = true,
    isClass = true,
  ): void {
    const finalClassName = dynamic ? `${className}-dynamic-theme` : className;

    this.removeDynamicClass(finalClassName, isClass);

    if (!this.styleElement) {
      this.styleElement = this.renderer.createElement('style');
      this.renderer.appendChild(document.head, this.styleElement);
    }

    const cssString = this.generateCssString(finalClassName, styles, isClass);
    this.styleMap.set(finalClassName, cssString);

    if (this.styleElement) {
      this.styleElement.innerHTML = Array.from(this.styleMap.values()).join(
        '\n',
      );
    }
  }

  private removeDynamicClass(className: string, isClass: boolean): void {
    if (this.styleElement) {
      const classRegex = new RegExp(
        `${isClass ? '.' : ''}${className}\\s*{[^}]*}\\n?`,
        'g',
      );
      this.styleElement.innerHTML = this.styleElement.innerHTML.replace(
        classRegex,
        '',
      );
    }
  }

  private generateCssString(
    className: string,
    styles: Record<string, string>,
    isClass: boolean,
  ): string {
    const cssProperties = Object.entries(styles)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ');
    return `${isClass ? '.' : ''}${className} { ${cssProperties} }\n`;
  }

  private ensureFontStylesheet(
    fontFamily: string,
    customFont?: boolean,
    customerId?: string,
  ): void {
    if (!fontFamily || !customerId || !customFont) {
      return;
    }

    if (!fontFamily.trim()) {
      return;
    }

    const href = getFontCssUrl(this.MEDIA_URL, customerId, fontFamily);
    if (this.fontStylesheetLinks.has(href)) {
      return;
    }

    const linkEl = this.renderer.createElement('link') as HTMLLinkElement;
    linkEl.rel = 'stylesheet';
    linkEl.href = href;

    this.renderer.appendChild(document.head, linkEl);
    this.fontStylesheetLinks.set(href, linkEl);
  }
}
